import {
    Component,
    h,
    Prop,
    Host,
    Element,
    Event,
    EventEmitter,
    State,
    forceUpdate,
    Watch,
} from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import { AuthClientInstance, getAuthClient } from "@trumpf/ux.utils";
import { decodeJwt } from "../../utils/jwt/decode-jwt";
import { authClientName } from "../sf-page-header/env-config";
import {
    AccountData,
    IdTokenData,
} from "../sf-page-header/api/salesforceApiClient";
import { META_NAV_ID_PROFILE } from "../sf-page-header/constants";
import { getDataFromScriptTag } from "../sf-page-header/utils";
import { LogoutIcon } from "./LogoutIcon";
import {
    getAccountAddRedirectUrl,
    getAccountSwitchRedirectUrl,
    parseJsonSafe,
} from "./utils";
import { AccountTypes } from "./constants";

/**
 * Component for switching user accounts in the Salesforce page header.
 * Displays user information and provides options to switch between accounts, add another account, or log out.
 * Emits events for account switching, adding accounts, hiding the popover, link clicks, and logout actions.
 */
@Component({
    tag: "ux-iws-sf-account-switch",
    styleUrl: "sf-account-switch.scss",
    shadow: true,
})
export class SfAccountSwitch {
    /** ID for the i18n provider subscription */
    private i18nSubscriberId: string = "";
    /** Reference to the auth client instance */
    private authClient: AuthClientInstance = getAuthClient(authClientName);
    private popoverElement!: HTMLUxPopoverElement;

    /** Reference to the host element */
    @Element() host: HTMLUxIwsSfAccountSwitchElement;

    /** Whether the popup is opened */
    @Prop({ reflect: true }) opened: boolean = false;

    /** Placement of the popover */
    @Prop() placement: string = "";

    /** Whether the account-switch should handle redirection */
    @Prop() readonly redirect: boolean = false;

    /** ID of the target element used for positioning */
    @Prop() targetId: string = META_NAV_ID_PROFILE;

    /** Additional links shown in the popover */
    @Prop({ mutable: true }) links: Array<{
        label: string;
        href: string;
    }> = [];

    /** Text for the profile link */
    @State() idTokenData: IdTokenData | null = null;

    /** Text for the profile link */
    @State() userName: string = "";

    /** List of available accounts */
    @State() availableAccounts: AccountData[] = [];

    /** The currently active account */
    @State() activeAccount: AccountData | undefined = undefined;

    /** Custom event that is emitted when the account-switch popover is closed */
    @Event({ bubbles: true, composed: true }) uxHide: EventEmitter<void>;

    /**
     * Custom event that is emitted when a link is clicked.
     * The `event.detail` provides the href of the clicked link.
     */
    @Event({ bubbles: true, composed: true }) uxLinkClick: EventEmitter<string>;
    /**
     * Custom event that is emitted when the user requests to switch the account.
     * The `event.detail` provides the account data of the selected account.
     */
    @Event({ bubbles: true, composed: true })
    uxAccountChange: EventEmitter<AccountData>;

    /** Custom event that is emitted when the user requests to add a new account */
    @Event({ bubbles: true, composed: true }) uxAccountAdd: EventEmitter<void>;

    /** Custom event that is emitted when the logout button was clicked */
    @Event({ bubbles: true, composed: true }) uxLogout: EventEmitter<void>;

    /**
     * Watcher for the 'opened' property. Adds or removes event listeners depending on popup state.
     */
    @Watch("opened")
    onOpenedChange(newValue: boolean) {
        document.removeEventListener("mousedown", this.onDocumentMouseDown);
        document.removeEventListener("touchstart", this.onDocumentMouseDown);
        window.removeEventListener("scroll", this.onViewportChange);
        window.removeEventListener("resize", this.onViewportChange);
        if (newValue) {
            document.addEventListener("mousedown", this.onDocumentMouseDown);
            document.addEventListener("touchstart", this.onDocumentMouseDown);
            window.addEventListener("scroll", this.onViewportChange);
            window.addEventListener("resize", this.onViewportChange);
        }
    }

    /**
     * Handles mouse down events on the document to close the popup when clicking outside.
     * @param event MouseEvent triggered by user interaction.
     */
    private onDocumentMouseDown = (event: MouseEvent) => {
        if (!this.opened) {
            document.removeEventListener("mousedown", this.onDocumentMouseDown);
            return;
        }
        const targetElement = document.getElementById(this.targetId);
        let shouldClose = true;
        if (this.host.contains(event.target as Node)) {
            shouldClose = false;
        } else if (
            targetElement &&
            targetElement.contains(event.target as Node)
        ) {
            shouldClose = false;
        }
        if (shouldClose) {
            this.opened = false;
            this.uxHide.emit();
        }
    };

    /**
     * Handles viewport changes (scroll/resize) to update the popover placement if open.
     */
    private onViewportChange = () => {
        if (this.opened) {
            this.popoverElement.updatePlacement();
        } else {
            window.removeEventListener("scroll", this.onViewportChange);
            window.removeEventListener("resize", this.onViewportChange);
        }
    };

    /**
     * Handles authentication state changes. Decodes the ID token and updates user/account information.
     * If no token is present, resets user and account data.
     */
    private onAuth = () => {
        const idToken = this.authClient.getIdToken();
        if (!idToken) {
            this.idTokenData = null;
            this.userName = "";
            this.availableAccounts = [];
            this.activeAccount = undefined;
            return;
        }
        this.idTokenData = decodeJwt<IdTokenData>(idToken);
        this.userName = this.idTokenData.email || this.idTokenData.name || "";
        this.availableAccounts =
            parseJsonSafe<AccountData[]>(
                this.idTokenData.custom_attributes?.availableAccounts
            ) || [];
        this.activeAccount = parseJsonSafe<AccountData>(
            this.idTokenData.custom_attributes?.effectiveAccount
        );
    };

    connectedCallback() {
        this.authClient.subscribe(this.onAuth);
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        this.authClient.unsubscribe(this.onAuth);
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        document.removeEventListener("mousedown", this.onDocumentMouseDown);
        document.removeEventListener("touchstart", this.onDocumentMouseDown);
        window.removeEventListener("scroll", this.onViewportChange);
        window.removeEventListener("resize", this.onViewportChange);
    }

    componentWillLoad() {
        if (this.links.length === 0) {
            const links =
                getDataFromScriptTag<Array<{ label: string; href: string }>>(
                    "profileLinks"
                ) || [];
            if (links.length > 0) {
                this.links = links;
            }
        }
        this.onAuth();
        this.onOpenedChange(this.opened);
    }

    /** Renders the additional links in the popover */
    private renderLinks = () => {
        if (!Array.isArray(this.links) || this.links.length === 0) {
            return null;
        }
        return this.links.map((link) => (
            <a
                key={link.href}
                href={link.href}
                onClick={(event: MouseEvent) => {
                    if (this.uxLinkClick.emit(link.href).defaultPrevented) {
                        event.preventDefault();
                    }
                }}
            >
                {link.label}
            </a>
        ));
    };

    /** Handles account change clicks, emits event and performs redirection if enabled */
    private handleAccountChangeClick = (account: AccountData) => {
        this.uxAccountChange.emit(account);
        if (this.redirect) {
            const redirectUrl = getAccountSwitchRedirectUrl(
                account,
                this.idTokenData?.iss
            );
            if (redirectUrl) {
                // Use window.top to ensure the redirection happens in the top-level context
                window.top.location.href = redirectUrl;
            }
        }
    };

    private handleAccountAddClick = () => {
        this.uxAccountAdd.emit();
        if (this.redirect) {
            const redirectUrl = getAccountAddRedirectUrl(this.idTokenData?.iss);
            if (redirectUrl) {
                window.top.location.href = redirectUrl;
            }
        }
    };

    private handleAccountUpgradeClick = () => {
        if (this.redirect) {
            const redirectUrl = getAccountAddRedirectUrl(this.idTokenData?.iss);
            if (redirectUrl) {
                window.top.location.href = redirectUrl;
            }
        }
    };

    render() {
        const { t } = i18nProvider;
        const alternativeAccounts = this.availableAccounts.filter(
            (account) => account.Id !== this.activeAccount?.Id
        );
        const hasAccounts =
            alternativeAccounts.length > 0 || !!this.activeAccount;
        const displayUpgradeAccount =
            !this.activeAccount ||
            [AccountTypes.BusinessProspect, AccountTypes.Consumer].includes(
                this.activeAccount.Type
            );

        const targetElement = document.getElementById(this.targetId);
        const logoutLabel = t("app.header.logoutButton", "Logout");
        const switchAccountLabel = t(
            "app.header.account.switchAccount",
            "Switch account"
        );
        const addAccountLabel = t(
            "app.header.account.addAccount",
            "Add another account"
        );
        const upgradeAccountLabel = t(
            "app.header.account.upgradeAccount",
            "Upgrade account"
        );
        return (
            <Host inert={!this.opened} aria-hidden={!this.opened}>
                <ux-popover
                    ref={(el) => (this.popoverElement = el)}
                    opened={this.opened}
                    placement={this.placement}
                    targetElement={targetElement}
                    closeOnSelect={false}
                    controlled={true}
                    data-placement={this.placement}
                    onUxHide={(event) => {
                        event.stopPropagation();
                        this.opened = false;
                        this.uxHide.emit();
                    }}
                >
                    <ux-popover-panel>
                        <div
                            id="panel"
                            onMouseDown={(event: MouseEvent) => {
                                // Prevents the popover from closing when clicking inside it
                                event.stopPropagation();
                            }}
                        >
                            <div id="header">
                                <span>{this.userName}</span>
                                <button
                                    id="logout"
                                    onClick={() => this.uxLogout.emit()}
                                    aria-label={logoutLabel}
                                    title={logoutLabel}
                                >
                                    <LogoutIcon />
                                </button>
                            </div>
                            <div id="links">{this.renderLinks()}</div>
                            {hasAccounts && (
                                <div id="accounts">
                                    <div class="title">
                                        {switchAccountLabel}
                                    </div>
                                    {this.activeAccount && (
                                        <div class="switch active">
                                            <span class="account-label">
                                                {this.activeAccount.Name}
                                            </span>
                                            <span class="account-active">
                                                {t(
                                                    "app.header.account.active",
                                                    "active"
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    {alternativeAccounts.map((account) => (
                                        <button
                                            key={account.Id}
                                            type="button"
                                            class="switch"
                                            onClick={() =>
                                                this.handleAccountChangeClick(
                                                    account
                                                )
                                            }
                                        >
                                            {account.Name}
                                            <ux-icon name="chevron-right"></ux-icon>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {displayUpgradeAccount && (
                                <button
                                    id="footer"
                                    onClick={this.handleAccountUpgradeClick}
                                >
                                    {upgradeAccountLabel}
                                </button>
                            )}
                            {!displayUpgradeAccount && (
                                <button
                                    id="footer"
                                    onClick={this.handleAccountAddClick}
                                >
                                    +&nbsp;
                                    {addAccountLabel}
                                </button>
                            )}
                        </div>
                    </ux-popover-panel>
                </ux-popover>
            </Host>
        );
    }
}
